<script>
import { useElixioConnectStore } from '../../stores/elixioConnect'
import EcButton from '../ui/EcButton.vue'

export default {
  name: 'ModuleDataBatchSync',
  components: { EcButton },
  props: {
    organization: { type: Object, required: true },
    service: { type: Object, required: true },
  },
  data() {
    const store = useElixioConnectStore()
    let batch = null
    const stored = localStorage.getItem('EC_BATCH')
    if (stored) {
      try {
        batch = JSON.parse(stored)
      } catch {
        batch = null
      }
    }

    return {
      store,
      STORAGE_KEY: 'EC_BATCH',
      loading: false,
      batch,
      intervalId: null,
      completeStatus: '',
      createdAfter: null,
      jobIcons: {
        customers: 'people',
        invoices: 'receipt',
        payments: 'credit-card',
      },
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    notify() {
      return this.$ecNotify || (() => {})
    },
    confirm() {
      return this.$ecConfirm || ((msg) => Promise.resolve(window.confirm(msg)))
    },
    progressPercent() {
      return this.batch?.progress ?? 0
    },
    jobs() {
      if (!this.batch) return []
      return [
        {
          id: 'customers',
          name: this.t('ELIXIO_CONNECT.ELIXIO_UPDATED_CUSTOMERS'),
          pending:
            !this.batch.progress ||
            (this.batch.progress !== 100 && this.batch.pendingJobs > 2),
        },
        {
          id: 'invoices',
          name: this.t('ELIXIO_CONNECT.ELIXIO_UPDATED_INVOICES'),
          pending:
            !this.batch.progress ||
            (this.batch.progress !== 100 && this.batch.pendingJobs > 1),
        },
        {
          id: 'payments',
          name: this.t('ELIXIO_CONNECT.ELIXIO_UPDATED_PAYMENTS'),
          pending: !this.batch.progress || this.batch.progress !== 100,
        },
      ]
    },
  },
  watch: {
    batch: {
      handler(newValue) {
        if (newValue) {
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(newValue))
        } else {
          localStorage.removeItem(this.STORAGE_KEY)
        }

        if (newValue?.cancelledAt) {
          this.completeStatus = 'canceled'
          this.notify(this.t('ELIXIO_CONNECT.ELIXIO_CONNECT_CANCELLED'), 'error')
          this.stopTimer()
        }

        if (newValue && newValue.progress === 100) {
          this.completeStatus = 'completed'
          this.notify(this.t('ELIXIO_CONNECT.ELIXIO_CONNECT_COMPLETED'), 'success')
          this.stopTimer()
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.batch) this.startTimer()
  },
  beforeDestroy() {
    if (this.intervalId) clearInterval(this.intervalId)
  },
  methods: {
    async connect() {
      const confirmed = await this.confirm(
        this.t('ELIXIO_CONNECT.ELIXIO_CONNECT_CONFIRM_THIS_SUBSCRIPTION'),
      )
      if (!confirmed) return

      try {
        this.loading = true
        const data = await this.store.syncBatch(
          this.organization.id,
          this.service.slug,
          this.createdAfter,
        )
        this.batch = data.batch
        this.startTimer()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    startTimer() {
      this.intervalId = setInterval(() => this.fetchBatch(), 5000)
    },
    async fetchBatch() {
      if (!this.batch?.id) return
      try {
        const data = await this.store.getBatch(this.organization.id, this.batch.id)
        this.batch = data
      } catch (e) {
        console.error(e)
        this.stopTimer()
      }
    },
    stopTimer() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }
      this.batch = null
    },
  },
}
</script>

<template>
  <div class="ec-batch-sync">
    <!-- Date picker -->
    <div class="ec-form-group">
      <label class="ec-form-label">
        <i class="bi bi-calendar3" />
        {{ t('ELIXIO_CONNECT.ELIXIO_START_IMPORT_AT') }}
      </label>
      <div class="ec-input">
        <input
          v-model="createdAfter"
          type="date"
          class="ec-input__field"
          :placeholder="t('ELIXIO_CONNECT.SELECT_DATE')"
        />
      </div>
    </div>

    <!-- No batch running -->
    <div v-if="!batch">
      <!-- Status alerts -->
      <div v-if="completeStatus === 'completed'" class="ec-batch-sync__alert ec-batch-sync__alert--success">
        <i class="bi bi-check-circle" />
        <span>{{ t('ELIXIO_CONNECT.ELIXIO_CONNECT_COMPLETED') }}</span>
        <button class="ec-batch-sync__alert-close" @click="completeStatus = ''">
          <i class="bi bi-x" />
        </button>
      </div>
      <div v-else-if="completeStatus === 'canceled'" class="ec-batch-sync__alert ec-batch-sync__alert--error">
        <i class="bi bi-x-circle" />
        <span>{{ t('ELIXIO_CONNECT.ELIXIO_CONNECT_CANCELLED') }}</span>
        <button class="ec-batch-sync__alert-close" @click="completeStatus = ''">
          <i class="bi bi-x" />
        </button>
      </div>

      <EcButton
        variant="primary"
        :loading="loading"
        :disabled="loading"
        icon="arrow-repeat"
        @click="connect"
      >
        {{ t('ELIXIO_CONNECT.ELIXIO_START_CONNECT') }}
      </EcButton>
    </div>

    <!-- Batch in progress -->
    <div v-else class="ec-batch-sync__progress">
      <!-- Progress header -->
      <div class="ec-batch-sync__progress-header">
        <span class="ec-batch-sync__progress-label">
          {{ t('ELIXIO_CONNECT.ELIXIO_CONNECT_TOTAL_PROGRESS') }}
        </span>
        <span class="ec-batch-sync__progress-value">{{ progressPercent }}%</span>
      </div>

      <!-- Progress bar -->
      <div class="ec-batch-sync__bar">
        <div
          class="ec-batch-sync__bar-fill"
          :class="{ 'ec-batch-sync__bar-fill--complete': progressPercent === 100 }"
          role="progressbar"
          :style="{ width: `${progressPercent}%` }"
          :aria-valuenow="progressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        />
      </div>

      <!-- Job list -->
      <div class="ec-batch-sync__jobs">
        <div
          v-for="job in jobs"
          :key="job.id"
          class="ec-batch-sync__job"
          :class="{ 'ec-batch-sync__job--done': !job.pending }"
        >
          <div class="ec-batch-sync__job-left">
            <i class="bi ec-batch-sync__job-icon" :class="`bi-${jobIcons[job.id] || 'box'}`" />
            <span class="ec-batch-sync__job-name">{{ job.name }}</span>
          </div>
          <span
            class="ec-batch-sync__job-badge"
            :class="job.pending ? 'ec-batch-sync__job-badge--pending' : 'ec-batch-sync__job-badge--done'"
          >
            <i v-if="!job.pending" class="bi bi-check-lg" />
            <span v-else class="ec-batch-sync__job-spinner" />
            {{ t(`ELIXIO_CONNECT.ELIXIO_CONNECT_STATUS_${job.pending ? 'PENDING' : 'DONE'}`) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
